import {
  buildApplyStageRoutePath,
  useDefineProviderRoute,
} from "@versatile-credit-apply/composition"
import { type RouteRecordRaw } from "vue-router"
import {
  ProviderConfigResponse,
  ScreenId,
  type StaticProviderKey,
} from "@versatile-credit-apply/omni-apply-client"
import type { ProviderStore } from "@versatile-credit-apply/sdk"
import { useProviderStore } from "@versatile-credit-apply/sdk"
import { StepperId } from "@/models/stepper/stepper-id"

const providerKey: StaticProviderKey = "bread_standard"
const { defineRoute } = useDefineProviderRoute(providerKey)

export const routes: RouteRecordRaw[] = [
  defineRoute({
    path: "/apply/:applyStage/verify-code",
    component: () =>
      import("@/providers/bread_standard/views/BreadVerifyCodeView.vue"),
  }),

  defineRoute({
    path: "/apply/:applyStage/responses/prequalified",
    inherit: true,
    redirect: (to) =>
      buildApplyStageRoutePath(
        to.params.providerKey as string,
        "/plan-selection",
        { params: to.params }
      ),
  }),

  defineRoute({
    name: "apply.prequalify-plan-selection",
    path: "/apply/:applyStage/plan-selection",
    inherit: "apply.responses.prequalified",
    meta: {
      screenId: ScreenId.OFFER_PRESENTATION_PREQUAL,
      // if bread was not in side-by-side (same cascade group as primary)
      // then the terms were shown separately with the previous provider
      // decision, and we shouldn't need to show it again. If Bread is the primary
      // then it shouldn't matter if it is true.
      showPreviousDecision: () => {
        const store: ProviderStore = useProviderStore()
        const config = store.currentProviderConfig
        return config?.cascadeGroup === store.cascade[0]!.cascadeGroup
      },
    },
    component: () =>
      import(
        "@/providers/bread_standard/views/BreadPrequalifiedPaymentPlansView.vue"
      ),
  }),

  defineRoute({
    name: "apply.checkout-intro",
    path: "/apply/:applyStage/checkout-intro",
    component: () =>
      import("@/providers/bread_standard/views/BreadCheckoutIntroView.vue"),
  }),

  defineRoute({
    path: "/apply/resume",
    inherit: true,
    component: () =>
      import("@/providers/bread_standard/views/BreadResumeView.vue"),
  }),

  defineRoute({
    name: "apply.checkout-plan-select",
    path: "/apply/:applyStage/checkout-plan-select",
    component: () =>
      import(
        "@/providers/bread_standard/views/BreadCheckoutPlanSelectView.vue"
      ),
    meta: {
      screenId: ScreenId.OFFER_SELECTION,
    },
  }),

  defineRoute({
    name: "apply.confirm-checkout",
    path: "/apply/:applyStage/confirm-checkout",
    component: () =>
      import("@/providers/bread_standard/views/BreadConfirmCheckoutView.vue"),
  }),

  defineRoute({
    path: "/apply/:applyStage/responses/approved",
    inherit: true,
    component: () =>
      import("@/providers/bread_standard/views/BreadApprovedView.vue"),
  }),

  defineRoute({
    path: "/apply/:applyStage/terms",
    inherit: true,
    component: () => import("@/views/apply/PrequalifyTermsView.vue"),
    meta: {
      showPreviousDecision: true,
    },
  }),

  defineRoute({
    path: "/apply/prequalify/intro",
    inherit: true,
    component: () => import("@/views/apply/PrequalifyTermsView.vue"),
    // Bread standalone goes to normal app form
    // If we're in a cascade then land on prequal agreement
    beforeEnter: (to) => {
      const store: ProviderStore = useProviderStore()
      const allConfigs: ProviderConfigResponse[] = store.cascade
      if (allConfigs[0]!.providerKey === providerKey) {
        const formPath = buildApplyStageRoutePath(
          to.params.providerKey as string,
          "/forms/application",
          { params: to.params }
        )
        return {
          path: formPath,
        }
      } else {
        return true
      }
    },
    meta: {
      showPreviousDecision: true,
      allowOptOut: true,
      step: "terms",
      stepper: StepperId.APPLY,
    },
  }),
]
